import React, { useState } from "react";
import { useDispatch } from "react-redux";

import { ExpressCheckoutElement } from "@stripe/react-stripe-js";
import { useStripe, useElements } from "@stripe/react-stripe-js";

import { Row, Col, Spin, Typography } from "antd";

import { openNotificationWithIcon } from "utils/Notification";
import { setSubmitting } from "redux/actions/loading.actions";
import { CheckCircleOutlined, WalletOutlined } from "@ant-design/icons";
import { MERCHANT } from "utils/enums";

const { Text } = Typography;

/**
 * DigitalWalletsPayment Component
 * Handles Apple Pay / Google Pay payments using Stripe Payment Intent (direct charge).
 * Counterpart to DigitalWallets which uses a Setup Intent to save payment methods.
 */
function DigitalWalletsPayment({
  clientSecret,
  onSuccess,
  onError,
  children
}) {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const [isReady, setIsReady] = useState(false);

  const [walletStatus, setWalletStatus] = useState("idle"); // idle | loading | success | error
  const [walletMessage, setWalletMessage] = useState("");


  const resolveMerchant = (expressPaymentType) => {
    switch (expressPaymentType) {
      case "apple_pay":
        return MERCHANT.ApplePay;
      case "google_pay":
        return MERCHANT.GooglePay;
      default:
        // Fall back to GooglePay to preserve existing behavior if a new wallet type appears.
        // Consider logging this so you catch unhandled wallet types.
        return MERCHANT.GooglePay;
    }
  };


  const handleWalletConfirm = async (event) => {
    if (!stripe || !elements) {
      console.log("Stripe or elements not loaded");
      return;
    }

    if (!clientSecret) {
      console.log("Client secret not available");
      openNotificationWithIcon("error", "Error!", "Payment intent not ready");
      return;
    }

    // Stripe passes the wallet type the user clicked on the event:
    // 'apple_pay' | 'google_pay' | 'link' | etc.
    const walletType = event?.expressPaymentType;
    const merchant = resolveMerchant(walletType);

    setWalletStatus("loading");
    setWalletMessage("");
    dispatch(setSubmitting(true));

    try {
      const { error, paymentIntent: confirmedPaymentIntent } =
        await stripe.confirmPayment({
          elements,
          redirect: "if_required",
          confirmParams: {
            return_url: window.location.href,
          },
        });

      if (error) {
        setWalletStatus("error");
        setWalletMessage(error.message);
        openNotificationWithIcon("error", "Error!", error.message);
        dispatch(setSubmitting(false));
        onError?.(error);
        return;
      }

      // Success - payment was processed
      if (
        confirmedPaymentIntent?.status === "succeeded" ||
        confirmedPaymentIntent?.status === "processing"
      ) {
        setWalletStatus("success");
        setWalletMessage("Payment processed successfully!");
        onSuccess?.(confirmedPaymentIntent, merchant);
      } else {
        setWalletStatus("error");
        const errorMsg =
          confirmedPaymentIntent?.error?.message ||
          "Payment could not be processed";
        setWalletMessage(errorMsg);
        openNotificationWithIcon("error", "Error!", errorMsg);
        onError?.(confirmedPaymentIntent?.error);
      }
    } catch (err) {
      console.error("Wallet payment error:", err);
      setWalletStatus("error");
      const errorMsg = "An error occurred. Please try again.";
      setWalletMessage(errorMsg);
      openNotificationWithIcon(
        "error",
        "Error!",
        "An error occurred while processing your payment."
      );
      onError?.(err);
    } finally {
      dispatch(setSubmitting(false));
    }
  };

  const onWalletLoadError = ({ error }) => {
    setIsReady(true); // Stop loading spinner if wallet fails to load
    setWalletStatus("error");
    const errorMsg =
      error.message || "No digital wallets are available in this browser.";
    setWalletMessage(errorMsg);
    console.error("Wallet load error:", error);
  };

  return (
    <div style={{ marginTop: 24 }}>
      <Row>
        <Col xs={24} md={children ? 12 : 24}>
          <div
            style={{
              backgroundColor: "#486baf09",
              border: "1px solid #D9D9D9",
              borderRadius: 8,
              padding: 20,
              marginBottom: 16,
              textAlign: "center",
            }}
          >
            <div style={{ marginBottom: 8 }}>
              <div
                style={{
                  height: 32,
                  width: 32,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#000",
                  borderRadius: 6,
                  margin: "0 auto",
                }}
              >
                <WalletOutlined style={{ color: "#fff", fontSize: 20 }} />
              </div>
            </div>
            <div
              style={{
                marginBottom: 8,
                fontSize: 16,
                fontWeight: 600,
                color: "#15213B",
                fontFamily: "tradeGothic",
              }}
            >
              Digital Wallets
            </div>
            <div
              style={{
                fontSize: 14,
                color: "#7F889A",
                marginBottom: 16,
                fontFamily: "tradeGothic",
              }}
            >
              Pay with Apple Pay or Google Pay — processed securely through Stripe. Options shown depend on your device.
            </div>
          </div>

          {clientSecret && (
            <div style={{ marginBottom: 16 }}>
              <ExpressCheckoutElement
                onConfirm={handleWalletConfirm}
                onLoadError={onWalletLoadError}
                onReady={()=>setIsReady(true)}
                options={{
                  clientSecret,
                  wallets: {
                    googlePay: "always",
                    applePay: "always",
                    link: "never",
                  },
                  paymentMethods: {
                    googlePay: "always",
                    applePay: "always",
                    link: "never",
                  },
                  layout: {
                    maxRows: 2,
                    maxColumns: 1
                  }
                }}
              />
              {
                !isReady && <Spin style={{ display: "block", margin: "20px auto" }} />
              }
            </div>
          )}

          {walletStatus === "loading" && (
            <div style={{ textAlign: "center", padding: 20 }}>
              <Spin />
              <Text style={{ marginLeft: 12 }}>Processing...</Text>
            </div>
          )}

          {walletStatus === "success" && (
            <div
              style={{
                backgroundColor: "#e8f5e9",
                color: "#388e3c",
                padding: 12,
                borderRadius: 8,
                marginTop: 12,
                textAlign: "center",
              }}
            >
              <CheckCircleOutlined style={{ marginRight: 8 }} />
              {walletMessage}
            </div>
          )}

          {walletStatus === "error" && (
            <div
              style={{
                backgroundColor: "#ffebee",
                color: "#d32f2f",
                padding: 12,
                borderRadius: 8,
                marginTop: 12,
                textAlign: "center",
              }}
            >
              {walletMessage}
            </div>
          )}
        </Col>
      </Row>

      {children}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 16,
          fontSize: 12,
          color: "#7F889A",
        }}
      >
        <svg viewBox="0 0 24 24" width="16" height="16" style={{ color: "#486baf" }}>
          <path
            d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"
            fill="currentColor"
          />
        </svg>
        Secured by Stripe. Your data is encrypted.
      </div>
    </div>
  );
}

export default DigitalWalletsPayment;